/**
 * CalendlyBookingModal Component
 * Feature 035: Calendly Consultation Scheduling
 *
 * Modal dialog that embeds a Calendly scheduler iframe and creates
 * an engagement record when a booking is completed.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { InlineWidget } from 'react-calendly';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useCreateCalendlyEngagement } from '../hooks/useCalendlyEngagement';

type CalendlyBookingModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  providerCompanyName: string;
  calendlyUrl: string;
  profileViewEngagementId?: string;
  onSuccess?: () => void;
};

export function CalendlyBookingModal({
  open,
  onOpenChange,
  providerId,
  providerCompanyName,
  calendlyUrl,
  profileViewEngagementId,
  onSuccess,
}: CalendlyBookingModalProps) {
  const { user } = useAuth();
  const [isBookingComplete, setIsBookingComplete] = useState(false);
  const [calendlyError, setCalendlyError] = useState(false);
  const [, setBookingDetails] = useState<{
    eventUrl?: string;
    scheduledAt?: string;
  }>({});

  const createEngagementMutation = useCreateCalendlyEngagement();

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setIsBookingComplete(false);
      setCalendlyError(false);
      setBookingDetails({});
    }
  }, [open]);

  // Listen for Calendly events
  useEffect(() => {
    const handleCalendlyEvent = (e: MessageEvent) => {
      // Validate origin to prevent XSS attacks
      if (e.origin !== 'https://calendly.com') return;

      // Only process Calendly events
      if (!e.data || typeof e.data !== 'string') return;

      try {
        const data = JSON.parse(e.data);

        // Check if this is a Calendly event_scheduled event
        if (data.event === 'calendly.event_scheduled') {
          setIsBookingComplete(true);

          // Extract booking details from payload
          const eventUrl = data.payload?.event?.uri || undefined;
          const scheduledAt = data.payload?.event?.start_time || undefined;

          setBookingDetails({ eventUrl, scheduledAt });

          // Create engagement in backend
          if (user?.id) {
            createEngagementMutation.mutate(
              {
                providerId,
                careSeekerId: user.id,
                calendlyEventUrl: eventUrl,
                scheduledAt,
                profileViewEngagementId,
              },
              {
                onSuccess: () => {
                  // Call success callback
                  if (onSuccess) {
                    onSuccess();
                  }

                  // Close modal after short delay
                  setTimeout(() => {
                    onOpenChange(false);
                  }, 2000);
                },
                onError: (error) => {
                  console.error('Failed to create engagement:', error);
                  // Show error but don't close modal - booking already happened
                },
              },
            );
          }
        }
      } catch (error) {
        // Ignore parsing errors - not all messages are JSON
      }
    };

    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [user?.id, providerId, createEngagementMutation, onSuccess, onOpenChange]);

  const handleClose = () => {
    if (!createEngagementMutation.isPending) {
      onOpenChange(false);
    }
  };

  const handleFallbackToForm = () => {
    // Fallback to traditional consultation request form when Calendly fails
    const params = new URLSearchParams();
    if (providerId) {
      params.set('providerId', providerId);
    }
    if (providerCompanyName) {
      params.set('providerCompanyName', providerCompanyName);
    }

    const queryString = params.toString();
    const fallbackUrl = queryString
      ? `/consultations/request?${queryString}`
      : '/consultations/request';

    // Navigate to the traditional consultation request form
    window.location.assign(fallbackUrl);

    // Close the Calendly modal
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl">Schedule Consultation</DialogTitle>
          <DialogDescription>
            Select a time that works for you from {providerCompanyName}'s calendar
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Success message */}
          {isBookingComplete && !createEngagementMutation.isError && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {createEngagementMutation.isSuccess ? (
                  <>Booking confirmed! Your consultation has been scheduled.</>
                ) : (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Confirming your booking...
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Engagement creation error */}
          {createEngagementMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your booking was successful in Calendly, but we couldn't save it to your bQuest
                account. Please contact the provider directly or check your Calendly confirmation
                email.
              </AlertDescription>
            </Alert>
          )}

          {/* Calendly error */}
          {calendlyError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="space-y-2">
                <p>Unable to load the scheduling calendar.</p>
                <Button variant="outline" size="sm" onClick={handleFallbackToForm}>
                  Use Traditional Request Form
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Calendly embed */}
          {!calendlyError && (
            <div className="w-full h-[600px] rounded-lg overflow-hidden">
              <InlineWidget
                url={calendlyUrl}
                styles={{ height: '100%', minWidth: '320px' }}
                prefill={{
                  email: user?.email,
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                  name: `${user?.firstName} ${user?.lastName}`,
                }}
                pageSettings={{
                  hideEventTypeDetails: false,
                  hideLandingPageDetails: false,
                }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
